import { createContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';



interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
    signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
    signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
    signInWithGoogle: () => Promise<{ error: Error | null }>;
    resetPassword: (email: string) => Promise<{ error: Error | null }>;
    signOut: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        let isMounted = true;

        // Safety timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
            if (isMounted) {
                console.warn('[AuthContext] Auth loading timeout reached (5s). Forcing loading to false.');
                setLoading(false);
            }
        }, 5000);

        // Note: onAuthStateChange fires immediately with the current session if one exists.
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                // Clear timeout if we get a response
                clearTimeout(timeoutId);

                if (!isMounted) return;

                try {
                    setSession(session);
                    setUser(session?.user ?? null);


                } catch (error) {
                    console.error('[AuthContext] Error processing auth state change:', error);
                } finally {
                    if (isMounted) {
                        // Only set loading to false if we are not aborted (actually we always want to set false eventually)
                        // But if a new request started, this finally block belongs to the OLD request?
                        // No, this is onAuthStateChange. It runs sequentially?
                        // Actually onAuthStateChange is an event listener. It can be called multiple times.
                        // But since we await fetchWorkspaces, this finally block runs after fetch finishes (or aborts).
                        setLoading(false);
                    }
                }
            }
        );

        // Handle visibility change to re-sync session when tab becomes active
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible' && isMounted) {
                try {
                    // Refresh session when tab becomes visible
                    const { error } = await supabase.auth.startAutoRefresh();
                    if (error) console.error('Error starting auto refresh:', error);
                } catch (error) {
                    console.error('[AuthContext] Error refreshing session on visibility change:', error);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            isMounted = false;
            subscription.unsubscribe();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const signIn = async (email: string, password: string) => {
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) {
                console.error('[AuthContext] signInWithPassword error:', error.message);
            }
            return { error: error as Error | null };
        } catch (e) {
            console.error('[AuthContext] signIn exception:', e);
            return { error: e as Error };
        }
    };

    const signUp = async (email: string, password: string, displayName?: string) => {
        const redirectUrl = `${window.location.origin}/dashboard`;
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                emailRedirectTo: redirectUrl,
                data: { display_name: displayName }
            }
        });
        return { error: error as Error | null };
    };

    const signInWithMagicLink = async (email: string) => {
        const redirectUrl = `${window.location.origin}/dashboard`;
        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                emailRedirectTo: redirectUrl,
            }
        });
        return { error: error as Error | null };
    };

    const signInWithGoogle = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: `${window.location.origin}/dashboard`
            }
        });
        return { error: error as Error | null };
    };

    const resetPassword = async (email: string) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/auth/reset-password`
        });
        return { error: error as Error | null };
    };

    const signOut = async () => {
        await supabase.auth.signOut();
    };



    return (
        <AuthContext.Provider value={{
            user,
            session,
            loading,
            signIn,
            signUp,
            signInWithMagicLink,
            signInWithGoogle,
            resetPassword,
            signOut
        }}>
            {children}
        </AuthContext.Provider>
    );
}
